import {
  computed,
  effect,
  inject,
  InjectionToken,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { filter, map, switchMap, take } from 'rxjs';
import { StoryService } from '../../stories/story.service';
import { AuthService } from '../../auth/auth.service';
import { Character } from '../../characters/types/character';

export const IMPERSONATE_CHAT_ID = new InjectionToken<number | undefined>(
  'IMPERSONATE_CHAT_ID',
);

const LS_KEY = 'chat_impersonate';

type ImpersonateStore = Record<string, number>;

function loadStore(): ImpersonateStore {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as ImpersonateStore) : {};
  } catch {
    return {};
  }
}

function getSavedImpersonate(chatId: number): number | undefined {
  return loadStore()[String(chatId)];
}

function setSavedImpersonate(
  chatId: number,
  characterId: number | undefined,
): void {
  const store = loadStore();
  if (characterId === undefined) {
    delete store[String(chatId)];
  } else {
    store[String(chatId)] = characterId;
  }
  localStorage.setItem(LS_KEY, JSON.stringify(store));
}

@Injectable()
export class SelectImpersonateService {
  private story = inject(StoryService, { optional: true });
  private chatId = inject(IMPERSONATE_CHAT_ID, { optional: true });

  profile = inject(AuthService).profile$;

  private _impersonateId = signal<number | undefined>(undefined);
  private restored = false;

  characters = toSignal(
    toObservable(this.story?.characters.data ?? signal([])).pipe(
      switchMap((list) =>
        this.profile.pipe(
          filter((p) => !!p),
          take(1),
          map((profile) => ({ profile, list })),
        ),
      ),
      map(
        ({ list, profile }) =>
          list?.filter((el) => el.owner === profile.id) ?? [],
      ),
    ),
    { initialValue: [] },
  );

  constructor() {
    effect(() => {
      const characters = this.characters();
      const chatId = this.chatId;
      if (!chatId || this.restored || characters.length === 0) return;

      const savedId = getSavedImpersonate(chatId);
      untracked(() => {
        this.restored = true;
        if (savedId && characters.some((c) => c.id === savedId)) {
          this._impersonateId.set(savedId);
        } else if (savedId) {
          setSavedImpersonate(chatId, undefined);
        }
      });
    });
  }

  setImpersonate(id: number | undefined) {
    this._impersonateId.set(id);
    const chatId = this.chatId;
    if (chatId != null) {
      setSavedImpersonate(chatId, id);
    }
  }

  impersonate = computed<Character | undefined>(() => {
    return this._impersonateId() === undefined
      ? undefined
      : (this.characters()?.find((el) => el.id === this._impersonateId()) ??
          undefined);
  });
}
